import { run, all, tx } from './sqlite';

/**
 * Minimal forward-only migration runner. Each migration is an idempotent SQL
 * block; version is tracked in the `schema_version` table.
 *
 * Why manual and not a library: we only need forward migrations, the app
 * owns the database, and we keep the WASM payload lean by not pulling in
 * a migration toolkit.
 */
const MIGRATIONS: Array<{ version: number; sql: string }> = [
  {
    version: 1,
    sql: `
      CREATE TABLE IF NOT EXISTS sessions (
        id TEXT PRIMARY KEY,
        title TEXT NOT NULL,
        template_id TEXT,
        status TEXT NOT NULL,
        owner_id TEXT NOT NULL,
        reviewer_id TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        submitted_at TEXT,
        approved_at TEXT,
        meta TEXT NOT NULL DEFAULT '{}'
      );

      CREATE TABLE IF NOT EXISTS captures (
        id TEXT PRIMARY KEY,
        session_id TEXT NOT NULL REFERENCES sessions(id) ON DELETE CASCADE,
        kind TEXT NOT NULL,
        blob_path TEXT,
        byte_size INTEGER NOT NULL DEFAULT 0,
        gps_json TEXT,
        tags_json TEXT NOT NULL DEFAULT '[]',
        ai_fields_json TEXT NOT NULL DEFAULT '{}',
        linked_field_id TEXT,
        payload_json TEXT NOT NULL DEFAULT '{}',
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );
      CREATE INDEX IF NOT EXISTS idx_captures_session ON captures(session_id, created_at);

      CREATE TABLE IF NOT EXISTS field_values (
        session_id TEXT NOT NULL REFERENCES sessions(id) ON DELETE CASCADE,
        field_id TEXT NOT NULL,
        value_json TEXT NOT NULL,
        source TEXT NOT NULL,
        confidence REAL,
        source_capture_id TEXT,
        updated_at TEXT NOT NULL,
        PRIMARY KEY (session_id, field_id)
      );

      CREATE TABLE IF NOT EXISTS sync_tasks (
        id TEXT PRIMARY KEY,
        op TEXT NOT NULL,
        entity_id TEXT NOT NULL,
        seq INTEGER NOT NULL,
        payload_json TEXT NOT NULL,
        blob_path TEXT,
        byte_size INTEGER NOT NULL DEFAULT 0,
        status TEXT NOT NULL DEFAULT 'queued',
        attempts INTEGER NOT NULL DEFAULT 0,
        last_error TEXT,
        base_version TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );
      CREATE INDEX IF NOT EXISTS idx_sync_status ON sync_tasks(status, seq);
    `,
  },
  {
    // Spec §7 "Client-side SQLite migration version: 2" — everything the pull
    // path needs to land server rows: an integer version for the concurrency
    // guard, soft-delete, org scoping, and the server's own timestamp kept
    // separate from the local one.
    //
    // SQLite can't ADD COLUMN ... NOT NULL without a default, hence the
    // defaults on org_id and version. org_id starts '' and is backfilled by
    // the first successful login, which is when the client learns its org.
    version: 2,
    sql: `
      ALTER TABLE sessions ADD COLUMN version INTEGER NOT NULL DEFAULT 1;
      ALTER TABLE sessions ADD COLUMN deleted_at TEXT;
      ALTER TABLE sessions ADD COLUMN org_id TEXT NOT NULL DEFAULT '';
      ALTER TABLE sessions ADD COLUMN server_updated_at TEXT;

      ALTER TABLE captures ADD COLUMN version INTEGER NOT NULL DEFAULT 1;
      ALTER TABLE captures ADD COLUMN deleted_at TEXT;
      ALTER TABLE captures ADD COLUMN org_id TEXT NOT NULL DEFAULT '';
      ALTER TABLE captures ADD COLUMN server_updated_at TEXT;
      ALTER TABLE captures ADD COLUMN media_id TEXT;
      ALTER TABLE captures ADD COLUMN media_url TEXT;

      ALTER TABLE field_values ADD COLUMN version INTEGER NOT NULL DEFAULT 1;
      ALTER TABLE field_values ADD COLUMN deleted_at TEXT;
      ALTER TABLE field_values ADD COLUMN org_id TEXT NOT NULL DEFAULT '';

      CREATE TABLE IF NOT EXISTS sync_watermarks (
        resource TEXT PRIMARY KEY,
        cursor TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS templates (
        id TEXT PRIMARY KEY,
        org_id TEXT NOT NULL,
        name TEXT NOT NULL,
        definition TEXT NOT NULL,
        version INTEGER NOT NULL DEFAULT 1,
        deleted_at TEXT,
        updated_at TEXT NOT NULL,
        server_updated_at TEXT
      );

      /*
       * Field-value ops used to be queued with entity_id = session id, which
       * can't address PUT /sessions/{sid}/fields/{fid}. Rewrite the ones
       * already sitting in a queue to the composite form rather than dropping
       * them — they represent work Sarah has done and not yet synced.
       */
      UPDATE sync_tasks
         SET entity_id = entity_id || ':' || json_extract(payload_json, '$.fieldId')
       WHERE op = 'fieldValue.upsert'
         AND instr(entity_id, ':') = 0
         AND json_extract(payload_json, '$.fieldId') IS NOT NULL;
    `,
  },
  {
    // The version the server reported when it rejected a push with 409.
    // Conflict resolution needs it to re-base a keep-local retry; without it
    // the retry re-sends the same stale base_version and conflicts forever.
    version: 3,
    sql: `
      ALTER TABLE sync_tasks ADD COLUMN server_version INTEGER;
    `,
  },
  {
    // Earliest time a retrying task may be dispatched again.
    //
    // Until now `attempts` was incremented and never read: there was no delay
    // between retries, no ceiling, and no way for a task to stop. On a browser
    // tab that mostly went unnoticed because Workbox's BackgroundSync queue
    // supplied some pacing at the service-worker layer. A phone on a flapping
    // radio has no such layer, so the schedule has to be explicit and durable
    // — in the database, not in memory, or a relaunch resets every backoff.
    //
    // NULL means "eligible now", which is what every existing queued row
    // should be, so no backfill is needed.
    version: 4,
    sql: `
      ALTER TABLE sync_tasks ADD COLUMN next_attempt_at TEXT;
      CREATE INDEX IF NOT EXISTS idx_sync_drain
        ON sync_tasks(status, next_attempt_at, seq);
    `,
  },
  {
    // Two columns, both fixing a way media uploads died silently.
    //
    // `mime` — the recorder's own answer for what it produced. It used to be
    // inferred from the stored file, and neither platform can do that: the
    // browser reports an OPFS `.webm` as `video/webm`, which the server's
    // allowlist refuses with a 415, and a 415 is terminal. Voice notes
    // recorded in Chromium never reached the server. NULL means "ask the
    // extension table", which is what already-queued rows get.
    //
    // `depends_on` — an explicit edge, replacing an assumption about `seq`.
    // A photo enqueued `media.upload` *before* `capture.upsert`, so the bytes
    // were POSTed for a capture the server had never seen; that answers 404,
    // 404 is terminal, and the photo stayed on the phone for ever. Ordering
    // by seq cannot express "after this specific task" once the drain runs
    // more than one task at a time, so the edge is now a column.
    version: 5,
    sql: `
      ALTER TABLE sync_tasks ADD COLUMN mime TEXT;
      ALTER TABLE sync_tasks ADD COLUMN depends_on TEXT;
      CREATE INDEX IF NOT EXISTS idx_sync_depends ON sync_tasks(depends_on);
    `,
  },
  {
    // When the server confirmed it holds the bytes.
    //
    // `captures.media_id` and `media_url` were added in v2 and nothing has
    // ever written to either: `recordServerVersion` looked up a version
    // target, media ops have none, and the upload response was dropped on the
    // floor. So the phone finished an upload with no local record that it had
    // happened — which is why the blobs could never be reclaimed. Deleting on
    // a bare `status = 'succeeded'` would have been guessing; a timestamp the
    // server's own response produced is not.
    //
    // Separate from v5 rather than appended to it because the runner is
    // forward-only and compares versions: any device that has already applied
    // v5 would skip an edited v5 for ever and then fail every query naming
    // the new column.
    version: 6,
    sql: `
      ALTER TABLE captures ADD COLUMN uploaded_at TEXT;
    `,
  },
  {
    /*
     * Where the session started.
     *
     * `Session.startLocation` has been in the shared Zod schema from the
     * beginning and had nowhere to live on either side — parsed, then dropped,
     * on the phone and on the server both. Server revision 0015 adds the
     * matching JSONB column.
     *
     * Stored as JSON text in one column rather than two REAL columns, matching
     * `captures.gps_json`: a coordinate is one value, and half of one is not a
     * location.
     */
    version: 7,
    sql: `
      ALTER TABLE sessions ADD COLUMN start_location_json TEXT;
    `,
  },
  {
    /*
     * Why a reviewer sent a session back.
     *
     * The phone could show that a submission had come back and not why, which
     * sends the inspector to find the reviewer rather than the defect. Server
     * revision 0016 adds the matching column alongside the
     * `changes_requested` status this exists to explain.
     */
    version: 8,
    sql: `
      ALTER TABLE sessions ADD COLUMN review_note TEXT;
    `,
  },
  {
    /*
     * Which fields the reviewer's note is about.
     *
     * `review_note` (v8) carries the reason in prose, which is where a reason
     * belongs but is not something the form can act on. This is the list the
     * form marks and can jump to. Server revision 0017 is the matching column.
     *
     * JSON text with a '[]' default rather than nullable: an empty list and
     * "no list" mean the same thing here, and one representation is one fewer
     * branch at every read.
     */
    version: 9,
    sql: `
      ALTER TABLE sessions ADD COLUMN flagged_fields_json TEXT NOT NULL DEFAULT '[]';
    `,
  },
  {
    /*
     * The active project, and the projects there are to choose between.
     *
     * `projects` holds what `GET /projects/mine` returned — the projects this
     * user is assigned to. It is reference data, replaced wholesale on each
     * pull rather than merged: the endpoint answers "which are mine now", and
     * a project dropping out of that answer is an unassignment, which a
     * row-by-row merge would never notice.
     *
     * No `version` column for the same reason. Nothing on the phone writes to
     * this table, so there is no local edit to protect and no conflict to
     * resolve.
     *
     * `sessions.project_id` is the other half: which project a session was
     * filed under. Nullable, because capture has never required a project and
     * this change does not start requiring one — a session captured with no
     * active project set syncs exactly as it does today and is filed by a
     * manager in the console, or not at all.
     */
    version: 10,
    sql: `
      CREATE TABLE IF NOT EXISTS projects (
        id TEXT PRIMARY KEY,
        slug TEXT NOT NULL,
        name TEXT NOT NULL,
        code TEXT NOT NULL,
        status TEXT NOT NULL DEFAULT 'active',
        default_template_id TEXT,
        default_template_name TEXT,
        my_session_count INTEGER NOT NULL DEFAULT 0,
        last_worked_at TEXT,
        synced_at TEXT NOT NULL
      );

      ALTER TABLE sessions ADD COLUMN project_id TEXT;

      CREATE INDEX IF NOT EXISTS idx_sessions_project ON sessions(project_id);
    `,
  },
  {
    /**
     * `captures.subject_ref`: which thing on site a capture is of.
     *
     * A column rather than a key inside `payload_json`, because grouping a
     * session's captures by subject is a query — the review screen renders
     * forty door bundles out of two hundred captures, and the workspace
     * answers "every session that mentioned this code". Neither can read a
     * JSON blob per row.
     *
     * Nullable, and null is the common case: a bridge inspection, a site
     * walkthrough and the establishing shot of a school all have no subject,
     * and nothing here starts requiring one.
     *
     * The index is on `(session_id, subject_ref)` rather than `subject_ref`
     * alone: the device only ever asks within one session, and the session is
     * what makes the scan selective on a table that grows without bound.
     */
    version: 11,
    sql: `
      ALTER TABLE captures ADD COLUMN subject_ref TEXT;

      CREATE INDEX IF NOT EXISTS idx_captures_subject
        ON captures(session_id, subject_ref);
    `,
  },
  {
    /**
     * `captures.captured_at`: when the device says this was taken.
     *
     * `created_at` already holds that for a capture made on *this* device, and
     * for a while that looked like enough. It is not, for two reasons that
     * pull in opposite directions.
     *
     * Going up: `captureBody()` never put `created_at` on the wire, so the
     * server stamped its own arrival time and every export, report and review
     * screen answered "when" with the sync moment. A plant-room survey walked
     * Monday and synced Wednesday read as Wednesday.
     *
     * Coming down: `applyCapture()` writes the server's `created_at` into rows
     * that arrive by pull. For a capture someone else took, that is the only
     * time we have and it is right to store it — but it is *their* sync time,
     * not their capture time. Sending it back up as this device's answer to
     * "when was this taken" would manufacture a capture time nobody recorded,
     * on precisely the field a record has to be able to defend.
     *
     * So this column is deliberately narrower than `created_at`. It only ever
     * holds a time some device reported *at the moment of capture*: written
     * here when the capture is taken, and carried down by pull from the
     * server's own `captured_at` — never derived from `created_at` on either
     * side. NULL therefore means "no device ever said", which is honest for a
     * row recorded before this migration, and the server treats NULL on the
     * way up as "leave whatever you have" rather than "clear it".
     */
    version: 12,
    sql: `
      ALTER TABLE captures ADD COLUMN captured_at TEXT;
    `,
  },
  {
    /**
     * `captures.ai_status` / `ai_detail`: why a capture produced no suggestions.
     *
     * The server has always known — a skipped extraction is a row in
     * `ai_extraction_runs`, not a silence — and the phone has never been told.
     * So a field with no suggestion looked the same whether the model had read
     * the photograph and found nothing, the org had extraction switched off,
     * the daily cap had been reached, the run had failed, or the worker simply
     * had not got to it yet. Those call for different next actions, and one of
     * them is not a problem at all.
     *
     * Defaulted to empty rather than backfilled: for every row already on a
     * device, "no run recorded" is the true answer, and it is the same thing
     * the UI says for a capture still waiting.
     */
    version: 13,
    sql: `
      ALTER TABLE captures ADD COLUMN ai_status TEXT NOT NULL DEFAULT '';
      ALTER TABLE captures ADD COLUMN ai_detail TEXT NOT NULL DEFAULT '';
    `,
  },
];

export async function runMigrations(): Promise<void> {
  await run(
    `CREATE TABLE IF NOT EXISTS schema_version (
       version INTEGER PRIMARY KEY,
       applied_at TEXT NOT NULL
     )`,
  );
  const rows = await all<{ v: number | null }>(`SELECT MAX(version) AS v FROM schema_version`);
  const current = rows[0]?.v ?? 0;

  for (const migration of MIGRATIONS) {
    if (migration.version <= current) continue;
    // One message, so the schema change and the version row that records it
    // cannot end up on opposite sides of a failure.
    await tx([
      { sql: migration.sql },
      {
        sql: `INSERT INTO schema_version (version, applied_at) VALUES (?, ?)`,
        bind: [migration.version, new Date().toISOString()],
      },
    ]);
  }
}
